import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { WorkspaceShell } from "./WorkspaceShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, CheckSquare } from "lucide-react";

const CATEGORIES = [
  ["freight", "Freight"], ["electrical", "Electrical"], ["furniture", "Furniture"],
  ["labor", "Labor"], ["warehouse", "Warehouse"], ["install", "Installation"],
  ["dismantle", "Dismantle"], ["return_ship", "Return Ship"], ["misc", "Miscellaneous"],
];
const STATUSES = [
  ["pending", "Pending"], ["in_progress", "In Progress"],
  ["blocked", "Blocked"], ["done", "Done"],
];
const STATUS_STYLE = {
  pending: "text-zinc-600 bg-zinc-100 border-zinc-200",
  in_progress: "text-[#A39171] bg-[#A39171]/10 border-[#A39171]/40",
  blocked: "text-red-700 bg-red-50 border-red-200",
  done: "text-emerald-700 bg-emerald-50 border-emerald-200",
};

export default function LogisticsWorkspace() {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState({ category: "", status: "" });
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/logistics-tasks")
    .then(({ data }) => setTasks(data.tasks || []))
    .catch(() => toast.error("Could not load tasks"));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      if (filter.category && t.category !== filter.category) return false;
      if (filter.status && t.status !== filter.status) return false;
      return true;
    });
  }, [tasks, filter]);

  const counts = useMemo(() => {
    const c = { pending: 0, in_progress: 0, blocked: 0, done: 0 };
    tasks.forEach((t) => { c[t.status] = (c[t.status] || 0) + 1; });
    return c;
  }, [tasks]);

  const update = async (id, patch) => {
    try {
      const { data } = await api.put(`/admin/logistics-tasks/${id}`, patch);
      setTasks((cur) => cur.map((t) => t.id === id ? data.task : t));
    } catch (e) { toast.error("Could not save"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/admin/logistics-tasks/${id}`);
      setTasks((cur) => cur.filter((t) => t.id !== id));
    } catch { toast.error("Could not delete"); }
  };

  const tabs = [
    {
      value: "board", label: "Task Board",
      render: () => (
        <div className="grid gap-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[hsl(var(--line))]" data-testid="log-kpis">
            <Kpi label="Pending" value={counts.pending} />
            <Kpi label="In Progress" value={counts.in_progress} accent />
            <Kpi label="Blocked" value={counts.blocked} />
            <Kpi label="Done" value={counts.done} />
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <Select value={filter.category || "all"} onValueChange={(v) => setFilter((f) => ({ ...f, category: v === "all" ? "" : v }))}>
              <SelectTrigger className="h-9 rounded-none w-48" data-testid="log-filter-category"><SelectValue placeholder="All categories" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {CATEGORIES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filter.status || "all"} onValueChange={(v) => setFilter((f) => ({ ...f, status: v === "all" ? "" : v }))}>
              <SelectTrigger className="h-9 rounded-none w-40" data-testid="log-filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <button onClick={() => setCreating(true)} className="btn-black h-9 px-4 text-xs ml-auto" data-testid="log-add-task">
              <Plus size={12} /> New Task
            </button>
          </div>

          <div className="card-flat overflow-x-auto" data-testid="log-table">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-left">
                <tr className="border-b border-zinc-200">
                  <Th>Task</Th><Th>Category</Th><Th>Owner</Th><Th>Due</Th><Th>Status</Th><Th></Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="border-b border-zinc-100 last:border-0" data-testid={`log-row-${t.id}`}>
                    <Td>
                      <div className="font-medium">{t.title}</div>
                      {t.notes && <div className="text-xs text-zinc-500 mt-1">{t.notes}</div>}
                    </Td>
                    <Td><span className="pill">{CATEGORIES.find(([k]) => k === t.category)?.[1] || t.category}</span></Td>
                    <Td>{t.owner || <span className="text-zinc-400">—</span>}</Td>
                    <Td className="tabular-nums text-xs">{t.due_date || "—"}</Td>
                    <Td>
                      <Select value={t.status} onValueChange={(v) => update(t.id, { status: v })}>
                        <SelectTrigger className={`h-8 rounded-none border ${STATUS_STYLE[t.status]}`} data-testid={`log-status-${t.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Td>
                    <Td>
                      <button onClick={() => remove(t.id)} className="text-zinc-400 hover:text-red-600" data-testid={`log-remove-${t.id}`} aria-label="Delete">
                        <Trash2 size={14} />
                      </button>
                    </Td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-zinc-500">
                  {tasks.length === 0 ? "No logistics tasks yet. Add one to get started." : "No tasks match the current filter."}
                </td></tr>}
              </tbody>
            </table>
          </div>

          {creating && <CreateTaskDialog onClose={() => setCreating(false)} onCreated={(t) => { setTasks((c) => [t, ...c]); setCreating(false); }} />}
        </div>
      ),
    },
  ];

  return (
    <WorkspaceShell
      title="Logistics"
      subtitle="Freight, electrical, furniture, labor, warehouse, install, dismantle — the physical event ops layer."
      tabs={tabs}
      dataTestId="ws-title-logistics"
    />
  );
}

function CreateTaskDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", category: "freight", owner: "", due_date: "", status: "pending", priority: "normal", notes: "", exhibitor_id: "" });
  const [busy, setBusy] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => { api.get("/admin/supplier-projects").then(({ data }) => setSuppliers(data.projects || [])).catch(() => {}); }, []);
  const create = async () => {
    if (form.title.trim().length < 3) return toast.error("Enter a task title.");
    setBusy(true);
    try {
      const body = { ...form };
      if (!body.exhibitor_id) delete body.exhibitor_id;
      const { data } = await api.post("/admin/logistics-tasks", body);
      toast.success("Task added.");
      onCreated(data.task);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Could not create task");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="log-new-modal">
      <div className="bg-white max-w-md w-full p-6 md:p-8">
        <div className="overline mb-2"><span className="accent-rule" />New Logistics Task</div>
        <div className="serif text-2xl mb-5">Add task</div>
        <div className="grid gap-4">
          <F label="Title *"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="log-new-title" className="h-11 rounded-none" /></F>
          <F label="Category">
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="h-11 rounded-none" data-testid="log-new-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Supplier (optional)">
            <Select value={form.exhibitor_id || "none"} onValueChange={(v) => setForm({ ...form, exhibitor_id: v === "none" ? "" : v })}>
              <SelectTrigger className="h-11 rounded-none" data-testid="log-new-supplier"><SelectValue placeholder="No supplier" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No supplier</SelectItem>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <div className="grid grid-cols-2 gap-4">
            <F label="Owner"><Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} placeholder="e.g. Carlos" data-testid="log-new-owner" className="h-11 rounded-none" /></F>
            <F label="Due Date"><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="log-new-due" className="h-11 rounded-none" /></F>
          </div>
          <F label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none" data-testid="log-new-notes" /></F>
        </div>
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="h-10 px-5 border border-zinc-300 text-xs uppercase tracking-[0.18em]" data-testid="log-new-cancel">Cancel</button>
          <button onClick={create} disabled={busy} className="btn-black h-10 px-5" data-testid="log-new-save">
            <CheckSquare size={12} /> {busy ? "…" : "Save Task"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`bg-white p-5 ${accent ? "border-l-2 border-[#A39171]" : ""}`}>
      <div className="serif text-3xl tabular-nums">{value ?? 0}</div>
      <div className="overline mt-2">{label}</div>
    </div>
  );
}
const Th = ({ children }) => <th className="text-xs uppercase tracking-[0.18em] text-zinc-500 px-4 py-3">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-4 align-top ${className}`}>{children}</td>;
function F({ label, children }) { return (<div><Label className="overline">{label}</Label><div className="mt-2">{children}</div></div>); }
